import { TemplateElement, html, defineElement, when, optionalAttribute } from '@webtides/element-js';
import style from './bento.css';
import { backgroundVideo } from '../../../../directives/video.js';
import { responsiveImage } from '../../../../directives/images.js';

/**
 * @typedef {Object} BaseCardProperties
 * @property {string} color
 * @property {string} kicker
 * @property {string} title
 * @property {string} text
 * @property {ImageProperties} image
 * @property {string} [video] - Background video URL (portrait card only); image is used as poster/fallback
 * @property {LinkProperties} [link] - Optional link that makes the whole card clickable
 */

/**
 * @typedef {Object} SquareCardProperties
 * @property {string} color
 * @property {string} text
 * @property {ImageProperties} image
 * @property {LinkProperties} [link] - Optional link that makes the whole card clickable
 */

const linkedText = (link, content) =>
    when(
        link?.url,
        html`<a
            href="${link?.url}"
            class="stretched-link ${link?.cssClasses || ''}"
            element="bento"
            ${optionalAttribute(link?.trackingLabel, 'tracking-label', link?.trackingLabel)}
            >${content}</a
        >`,
        content,
    );

const linkedImage = (link, content) =>
    when(
        link?.url,
        html`<a
            href="${link?.url}"
            class="stretched-link block w-full h-full min-h-0 ${link?.cssClasses || ''}"
            element="bento"
            ${optionalAttribute(link?.text, 'aria-label', link?.text)}
            ${optionalAttribute(link?.trackingLabel, 'tracking-label', link?.trackingLabel)}
            >${content}</a
        >`,
        content,
    );

/**
 * @property {BaseCardProperties} portraitCard
 * @property {BaseCardProperties} landscapeCard
 * @property {SquareCardProperties} squareCard1
 * @property {SquareCardProperties} squareCard2
 */
export default class Bento extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            portraitCard: null,
            landscapeCard: null,
            squareCard1: null,
            squareCard2: null,
        };
    }

    template() {
        const squareCards = [this.squareCard1, this.squareCard2];

        const portraitHasVideo = !!this.portraitCard.video;
        const portraitHasImage = !!this.portraitCard.image?.src;
        const portraitHasMedia = portraitHasVideo || portraitHasImage;
        const portraitLinkOnTitle = !!this.portraitCard.title;
        const landscapeLinkOnTitle = !!this.landscapeCard.title;
        const landscapeLinkOnImage = !this.landscapeCard.title && !this.landscapeCard.kicker;
        const landscapeImage = html`<img
            loading="lazy"
            src="${this.landscapeCard.image?.src}"
            srcset="${this.landscapeCard.image?.srcset}"
            sizes="(min-width: 1440px) 340px, (min-width: 1024px) 25vw, 75vw"
            alt="${this.landscapeCard.image?.alt}"
            class="h-full w-full object-cover rounded-lg lg:rounded-r-lg lg:rounded-l-none"
        />`;

        return html`
            <div class="flex flex-col lg:grid lg:grid-cols-10 gap-fix-m">
                <figure
                    class="col-span-4 w-full relative m-0 rounded-[var(--mdx-sys-border-radius-lg)] overflow-hidden aspect-square lg:aspect-auto min-h-0 ${when(
                        !portraitHasMedia && this.portraitCard.color === 'light',
                        'bg-primary-subtle1 text-primary',
                        'bg-primary text-primary-on-default',
                    )}"
                >
                    ${when(
                        portraitHasVideo,
                        backgroundVideo(this.portraitCard.video, { poster: this.portraitCard.image?.src }),
                        when(
                            portraitHasImage,
                            responsiveImage(
                                this.portraitCard.image,
                                'w-full h-full object-cover',
                                false,
                                'auto',
                                '(min-width: 1024px) 40vw, 100vw',
                            ),
                        ),
                    )}
                    ${when(
                        portraitHasMedia,
                        html`
                            <div
                                class="absolute inset-0"
                                style="background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 68%, rgba(0, 0, 0, 0.38) 73.76%, rgba(0, 0, 0, 0.50) 91.04%);"
                            />
                        `,
                    )}

                    <figcaption class="absolute inset-0 flex flex-col justify-end p-fix-m">
                        ${when(
                            this.portraitCard.kicker,
                            html`
                                <p class="fix-body2 mb-0 mt-0">
                                    ${
                                        portraitLinkOnTitle
                                            ? this.portraitCard.kicker
                                            : linkedText(this.portraitCard.link, this.portraitCard.kicker)
                                    }
                                </p>
                            `,
                        )}
                        ${when(
                            this.portraitCard.title,
                            html`
                                <p class="fix-title2-medium mt-fix-2xs mb-0 text-balance">
                                    ${linkedText(this.portraitCard.link, this.portraitCard.title)}
                                </p>
                            `,
                        )}
                        ${when(
                            this.portraitCard.text,
                            html`
                                <p class="fix-body2 font-normal mt-fix-2xs mb-0 text-balance">
                                    ${this.portraitCard.text}
                                </p>
                            `,
                        )}
                    </figcaption>
                </figure>
                <div class="col-span-6 flex flex-col lg:grid grid-cols-1 lg:grid-cols-2 grid-rows-2 gap-fix-m">
                    <div
                        class="relative col-span-2 rounded-[var(--mdx-sys-border-radius-lg)] ${when(
                            this.landscapeCard.color === 'light',
                            'bg-primary-subtle1 text-primary',
                            'bg-primary text-primary-on-default',
                        )}"
                    >
                        <div class="flex flex-col lg:flex-row items-stretch gap-0 h-full">
                            <div
                                class="pt-fix-s pb-0 px-fix-s lg:self-end lg:pt-fix-m lg:pb-fix-m lg:pl-fix-m lg:pr-0 grow"
                            >
                                ${when(
                                    this.landscapeCard.kicker,
                                    html`
                                        <p class="fix-body2-medium mb-0 mt-0">
                                            ${
                                                landscapeLinkOnTitle
                                                    ? this.landscapeCard.kicker
                                                    : linkedText(this.landscapeCard.link, this.landscapeCard.kicker)
                                            }
                                        </p>
                                    `,
                                )}
                                ${when(
                                    this.landscapeCard.title,
                                    html`
                                        <p class="fix-title2-medium mb-0 mt-fix-2xs text-balance">
                                            ${linkedText(this.landscapeCard.link, this.landscapeCard.title)}
                                        </p>
                                    `,
                                )}
                                ${when(
                                    this.landscapeCard.text,
                                    html`
                                        <p class="fix-body2 font-normal mb-0 mt-fix-2xs text-balance">
                                            ${this.landscapeCard.text}
                                        </p>
                                    `,
                                )}
                            </div>
                            ${when(
                                this.landscapeCard.image?.src,
                                html`
                                    <div
                                        class="overflow-hidden [contain:size] aspect-[4/5] w-3/4 mx-auto lg:mx-0 lg:w-auto lg:h-full lg:shrink-0"
                                    >
                                        ${
                                            landscapeLinkOnImage
                                                ? linkedImage(this.landscapeCard.link, landscapeImage)
                                                : landscapeImage
                                        }
                                    </div>
                                `,
                            )}
                        </div>
                    </div>
                    ${squareCards.map((card, index) => {
                        const hasImage = card.image?.src;
                        const hasText = card.text;
                        const image = html`<img
                            loading="lazy"
                            src="${card.image?.src}"
                            srcset="${card.image?.srcset}"
                            sizes="(min-width: 1440px) 340px, (min-width: 1024px) 30vw, 100vw"
                            alt="${card.image?.alt}"
                            class="min-h-0 w-full object-cover ${when(
                                hasText,
                                'rounded-b-lg flex-1',
                                'rounded-[var(--mdx-sys-border-radius-lg)] h-full',
                            )}"
                        />`;
                        return html`
                            <div
                                class="relative h-full rounded-[var(--mdx-sys-border-radius-lg)] flex flex-col items-center justify-center text-center col-span-1 aspect-square w-full gap-fix-m ${when(
                                    card.color === 'light',
                                    'bg-primary-subtle1 text-primary',
                                    'bg-primary text-primary-on-default',
                                )}"
                            >
                                ${when(
                                    hasText,
                                    html`<p
                                        class="shrink-0 content-center lg:content-end mb-0 px-fix-m pt-fix-m ${when(
                                            hasImage,
                                            'fix-body1',
                                            'fix-title2-medium',
                                        )}"
                                    >
                                        ${linkedText(card.link, card.text)}
                                    </p>`,
                                )}
                                ${when(hasImage, hasText ? image : linkedImage(card.link, image))}
                            </div>
                        `;
                    })}
                </div>
            </div>
        `;
    }
}

defineElement('mb-bento', Bento);
